import React, { ReactNode, useEffect, useState } from 'react';

import cls from 'classnames';

import { withLayoutContext } from '@ott/layout-context';
import Lazy from '@ott/lazy';

import AppsSection from './blocks/AppsSection';
import CommunicationSection from './blocks/CommunicationSection';
import {
  AdditionalMobileMenuItem,
  BusinessMenuItem,
  ClientsMenuItem,
  CompanyMenuItem,
  PartnershipMenuItem,
  ProjectsMenuItem,
} from './blocks/CommunicationSection/helpers/getMenuItems';
import EmailSubscriptionSection from './blocks/EmailSubscriptionSection';
import InfoSection from './blocks/InfoSection';

import styles from './Footer.scss';

export type Lang =
  | 'ru'
  | 'uk-ua'
  | 'ru-ua'
  | 'pl'
  | 'tr'
  | 'ru-by'
  | 'ru-kz'
  | 'kz'
  | 'en-us'
  | 'en-gb'
  | 'de'
  | 'en-ie'
  | 'es'
  | 'ru-us'
  | 'ru-uz';

export type SeoItemLink = {
  text: string;
  url: string;
  target?: string;
  nofollow?: boolean;
};

export type SeoItem = {
  links: SeoItemLink[];
  header: string;
};

export type MenuItem =
  | ClientsMenuItem
  | BusinessMenuItem
  | PartnershipMenuItem
  | ProjectsMenuItem
  | CompanyMenuItem
  | AdditionalMobileMenuItem;

export type FooterProps = {
  className?: string;
  /**
   * Отображать ли блок с email подпиской
   */
  hasEmailSubscription?: boolean;
  /**
   * Отображать ли меню
   */
  hasMenu?: boolean;
  /**
   * Отображать ли блок со ссылками на мобильные приложения
   */
  hasMobileApps?: boolean;
  /**
   * Отображать ли блок с логотипами систем оплаты
   */
  hasPayments?: boolean;
  /**
   * Отображать ли блок с социальными сетями
   */
  hasSocials?: boolean;
  /**
   * Какие элементы меню нужно спрятать
   */
  hiddenMenuItems?: MenuItem[];
  hostname?: string;
  isMobile?: boolean;
  lang: Lang;
  languageSwitcher?: ReactNode;
  partner?: { [key: string]: any };
  preCopyright?: ReactNode;
  /**
   * Если передаем seoItems, то элементы меню заменяются на seoItems
   */
  seoItems?: SeoItem[];
  /**
   * домен отт+бренда
   */
  ottBrandSupportUrl?: string;
  /** Ссылка на политику конфиденциальности */
  privacyPolicyUrl?: string;
  specificLocators?: string | string[];
};

export const Footer = ({
  className,
  hasEmailSubscription = false,
  hasMenu = true,
  hasMobileApps = true,
  hasPayments = true,
  hasSocials = true,
  hiddenMenuItems = [],
  hostname = '',
  isMobile,
  lang,
  languageSwitcher,
  partner,
  preCopyright = null,
  seoItems = [],
  ottBrandSupportUrl,
  privacyPolicyUrl,
  specificLocators,
}: FooterProps) => {
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  return (
    <footer className={cls(styles.container, className)} data-locator={specificLocators}>
      <CommunicationSection
        isMobile={isMobile && isClient}
        hasMenu={hasMenu}
        hostname={hostname}
        lang={lang}
        partner={partner}
        hiddenMenuItems={hiddenMenuItems}
        seoItems={seoItems}
        ottBrandSupportUrl={ottBrandSupportUrl}
        privacyPolicyUrl={privacyPolicyUrl}
      />
      {hasEmailSubscription && (
        <Lazy containerClassName={styles.emailSubscriptionEmptyContainer}>
          <EmailSubscriptionSection />
        </Lazy>
      )}
      {hasMobileApps && (
        <Lazy containerClassName={styles.appSectionEmptyContainer}>
          <AppsSection />
        </Lazy>
      )}
      <Lazy containerClassName={styles.infoSectionEmptyContainer}>
        <InfoSection
          lang={lang}
          preCopyright={preCopyright}
          partnerCopyright={partner?.copyright}
          languageSwitcher={languageSwitcher}
          hasPayments={hasPayments}
          hasSocials={hasSocials}
        />
      </Lazy>
    </footer>
  );
};

export default React.memo(withLayoutContext(Footer));
